/**
 * Registration Campaign API Service
 *
 * Provides methods for managing tenant Registration Campaign policy.
 * Registration Campaign forces users to register Microsoft Authenticator
 * on first sign-in, preventing them from getting blocked by Conditional
 * Access policies later.
 *
 * Uses Bearer token authentication via MSAL through ApiService.
 */

import { ApiService } from './api';
import type {
  RegistrationCampaignConfig,
  RegistrationCampaignStatus,
} from '../types/registrationCampaign';

/**
 * Registration Campaign API Client
 *
 * Manages tenant-wide Registration Campaign policy settings.
 * All methods automatically include Authorization header via ApiService.
 */
class RegistrationCampaignApiServiceClass {
  /**
   * Get base URL for registration campaign endpoints.
   *
   * @param tenantId - Azure AD tenant identifier
   * @returns Base API URL
   */
  private getBaseUrl(tenantId: string): string {
    return `/api/tenants/${tenantId}/security/policies/registration-campaign`;
  }

  /**
   * Get Registration Campaign status for tenant.
   *
   * Retrieves current Registration Campaign configuration including
   * enabled state and snooze duration.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @returns Registration Campaign status with enabled state and snooze days
   * @throws Error if request fails
   */
  async getRegistrationCampaignStatus(
    tenantId: string
  ): Promise<RegistrationCampaignStatus> {
    const response = await ApiService.request<RegistrationCampaignStatus>(
      this.getBaseUrl(tenantId),
      {
        method: 'GET',
      }
    );

    return response;
  }

  /**
   * Configure Registration Campaign for tenant.
   *
   * Enable or disable Registration Campaign that forces users to register
   * Microsoft Authenticator on first sign-in. Prevents users from skipping
   * setup and getting blocked by Conditional Access policies later.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @param config - Registration Campaign configuration (enabled and snooze_days)
   * @returns Updated Registration Campaign status
   * @throws Error if request fails or validation error
   */
  async configureRegistrationCampaign(
    tenantId: string,
    config: RegistrationCampaignConfig
  ): Promise<RegistrationCampaignStatus> {
    const response = await ApiService.request<RegistrationCampaignStatus>(
      this.getBaseUrl(tenantId),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      }
    );

    return response;
  }
}

export const RegistrationCampaignApiService = new RegistrationCampaignApiServiceClass();
